import { convexQuery } from "@convex-dev/react-query";
import {
  applyPhoneNumberMask,
  usePhoneNumberMask,
} from "@plasma/utils/phone-numbers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { type ControllerRenderProps, useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  organizationsGetBusinessInformation,
  organizationsUpdateBusinessInformation,
} from "~/api-refs";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { convexClient } from "~/integrations/convex/provider";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/settings/business-information/edit/$type"
)({
  component: EditBusinessInformationModal,
});

interface BusinessInformationFormData {
  businessName: string;
  phoneNumber: string;
}

function EditBusinessInformationModal() {
  const { type } = Route.useParams();
  const navigate = useNavigate();

  // Validate type parameter
  const validType =
    type === "businessName" || type === "phoneNumber" ? type : "businessName";

  const { data: organizationQuery, isPending } = useQuery(
    convexQuery(organizationsGetBusinessInformation, {})
  );

  const organizationMutation = useMutation({
    mutationFn: (data: { businessName: string; phoneNumber: string }) => {
      return convexClient.mutation(
        organizationsUpdateBusinessInformation,
        data
      );
    },
    onSuccess: () => {
      toast.success("Business information updated");
    },
  });

  const form = useForm<BusinessInformationFormData>({
    defaultValues: {
      businessName: "",
      phoneNumber: "",
    },
  });

  useEffect(() => {
    if (organizationQuery) {
      form.reset({
        businessName: organizationQuery.businessName ?? "",
        phoneNumber: organizationQuery.phoneNumber
          ? applyPhoneNumberMask(organizationQuery.phoneNumber)
          : "",
      });
    }
  }, [form, organizationQuery]);

  const handleClose = () => {
    void navigate({ to: "/settings/business-information" });
  };

  const onSubmit = async (data: BusinessInformationFormData) => {
    try {
      await organizationMutation.mutateAsync({
        businessName: data.businessName,
        phoneNumber: data.phoneNumber.replace(/\D/g, ""),
      });
      // Track settings update in PostHog
      ProductEvents.settingsUpdated("business_information");
      form.reset();
      handleClose();
    } catch (err) {
      toast.error("Error updating business information", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  };

  if (isPending) {
    return (
      <ModalLoadingDialog
        message="Loading business information..."
        onOpenChange={handleClose}
      />
    );
  }

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent>
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>
            Edit{" "}
            {validType === "businessName" ? "Business Name" : "Phone Number"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
            {validType === "businessName" ? (
              <div>
                <FormField
                  control={form.control}
                  name="businessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={organizationQuery === undefined}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : (
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => <PhoneNumberInput field={field} />}
              />
            )}

            <DialogFooter>
              <Button
                className="w-full"
                onClick={handleClose}
                size="lg"
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                className="w-full"
                disabled={organizationMutation.isPending}
                size="lg"
                type="submit"
              >
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

const PhoneNumberInput = ({
  field,
}: {
  field: ControllerRenderProps<BusinessInformationFormData, "phoneNumber">;
}) => {
  const inputMaskRef = usePhoneNumberMask();
  useEffect(() => {
    field.ref(inputMaskRef.current);
  }, [field, inputMaskRef]);
  return (
    <FormItem>
      <FormLabel>Business phone number</FormLabel>
      <FormControl>
        <Input
          {...field}
          autoComplete="tel"
          inputMode="tel"
          ref={inputMaskRef}
          type="text"
        />
      </FormControl>
      <FormMessage />
    </FormItem>
  );
};
